/**
 * ============================================================================
 * KbAclManager — Gestão de ACL do KB (v3 Steel Split-pane).
 *
 * Layout fiel ao protótipo aprovado pelo usuário:
 *   - Header com ícone em caixa + título "Controle de acesso".
 *   - Sidebar esquerda (w-72) "Visualizar por sujeito" com botões
 *     (Membros, Perfis, Externos) + contadores, e card CTA
 *     "Precisa de um novo perfil? → Gerenciar Perfis".
 *   - Main: toolbar (busca + botão "Conceder acesso" via Popover),
 *     tabela densa (Sujeito / Nível / Origem / Ação) com header sticky.
 *   - Footer: contador de usuários + indicador "Alterações salvas
 *     automaticamente" + KBID abreviado.
 *
 * Reaproveita RPCs existentes:
 *   - kb_list_page_access   (via useKbAclList)
 *   - kb_grant_page_access  (via useKbGrantAccess)
 *   - kb_revoke_page_access (via useKbRevokeAccess)
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-09 v3.0.4 - Listagem passa a renderizar SmartAvatar com o
 *            avatar real do membro (target_avatar_url da RPC v1.2.0). Externos
 *            e perfis mantêm fallback (iniciais/ícone). Padroniza com as
 *            demais listagens do sistema (agentes, etc.).
 *   RELEASE: 2026-07-09 v3.0.3 - Substitui `<Select>` do campo "Alvo" por
 *            combobox com busca (Popover + Command + CommandInput), avatar
 *            (SmartAvatar) + nome + email, alinhado ao padrão
 *            SystemUserSelector/UserSelector. Aplica-se a membros, externos
 *            e perfis (perfil usa ícone no lugar do avatar).
 *   RELEASE: 2026-07-09 v3.0.2 - Remove SELECT direto do frontend a
 *            `user_roles` (quebrava com PGRST200 por falta de FK para
 *            `profiles`) e a `kb_external_users` (filtrava coluna `status`
 *            inexistente). Passa a usar `get_tenant_members_safe` (RPC
 *            oficial já existente) e a nova `kb_acl_list_external_users`.
 *            Renomeia "tenant"→"empresa" nos rótulos visíveis.
 *   RELEASE: 2026-07-09 v3.0.1 - Remove CTA "Gerenciar Perfis" da sidebar
 *            (risco de exposição indevida).
 *   RELEASE: 2026-07-09 v3.0.0 - Reimplementado fielmente sobre o protótipo
 *            "Steel Split-pane": header próprio, sidebar de tipos com
 *            contadores em pílula, toolbar com Popover de conceder,
 *            tabela com colunas Sujeito/Nível/Origem/Ação e footer de status.
 *            Paleta Slate & Steel via tokens do design system + Sora/Manrope.
 *   RELEASE: 2026-07-09 v2.0.0 - Redesign premium (versão anterior).
 *   RELEASE: 2026-07-08 v1.2.0 - Refatoração visual: prop `embedded`.
 *   RELEASE: 2026-07-08 v1.1.0 - Fase D: sujeito 'profile'.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (Fase C).
 * ============================================================================
 */
import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  Trash2, Users, Globe, KeyRound, Search, Plus, ShieldCheck,
  Info, Lock, ListFilter, Check, ChevronsUpDown,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SmartAvatar } from "@/components/ui/smart-avatar";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useKbAclList, useKbGrantAccess, useKbRevokeAccess,
  type KbAclScope, type KbAclTargetType, type KbAclLevel,
} from "@/hooks/kb/useKbAcl";

// ---------------------------------------------------------------------------
// Consultas auxiliares (mantidas do v2 — preservam contratos)
// ---------------------------------------------------------------------------

/** Membros ativos da empresa (para combobox). Reutiliza a RPC oficial. */
function useTenantMembersLite() {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ["kb-acl", "tenant-members", currentTenant?.id],
    enabled: !!currentTenant?.id,
    queryFn: async () => {
      // RPC padrão v2.1 — SECURITY DEFINER, resolve tenant via p_tenant_hint.
      const { data, error } = await supabase.rpc("get_tenant_members_safe", {
        p_tenant_hint: currentTenant!.id,
        p_only_active: true,
      });
      if (error) throw error;
      return ((data ?? []) as any[]).map((m) => ({
        id: m.user_id as string,
        label: (m.full_name || m.email || (m.user_id as string).slice(0, 8)) as string,
        sub: (m.email ?? undefined) as string | undefined,
        avatar_url: (m.avatar_url ?? null) as string | null,
      }));
    },
    staleTime: 60_000,
  });
}

/** Contatos externos ativos da empresa. Usa RPC dedicada (v3.0.2). */
function useExternalUsersLite() {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ["kb-acl", "external-users", currentTenant?.id],
    enabled: !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kb_acl_list_external_users", {
        p_search: null,
        p_limit: 200,
        p_tenant_hint: currentTenant!.id,
      } as any);
      if (error) throw error;
      return ((data ?? []) as any[]).map((u) => ({
        id: u.id as string,
        label: (u.display_name || u.email || (u.id as string).slice(0, 8)) as string,
        sub: (u.email ?? undefined) as string | undefined,
        avatar_url: null as string | null,
      }));
    },
    staleTime: 60_000,
  });
}

/** Perfis de permissão do tenant. */
function usePermissionProfilesLite() {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ["kb-acl", "permission-profiles", currentTenant?.id],
    enabled: !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_permission_profiles_list", {
        p_tenant_hint: currentTenant!.id,
      } as any);
      if (error) throw error;
      return ((data ?? []) as any[])
        .filter((p) => p.is_active)
        .map((p) => ({ id: p.id as string, label: p.name as string }));
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Meta / rótulos
// ---------------------------------------------------------------------------
interface Props {
  scope: KbAclScope;
  scopeId: string;
  canManage?: boolean;
  /** Quando true, esconde o header próprio (uso dentro do KbGovernanceSidebar). */
  embedded?: boolean;
  /** Nome do escopo (ex.: "Manutenções"). */
  scopeLabel?: string;
  /** Rótulo do tipo do escopo (ex.: "Space", "Coleção", "Página"). */
  scopeTypeLabel?: string;
}

const LEVEL_LABEL: Record<KbAclLevel, string> = {
  read: "Leitura",
  comment: "Comentário",
  edit: "Edição",
  admin: "Admin",
};

const TARGET_LABEL: Record<KbAclTargetType, string> = {
  member: "Membro da empresa",
  external: "Contato externo",
  group: "Grupo (avançado)",
  profile: "Perfil de permissão",
};

const TARGET_ICON: Record<KbAclTargetType, React.ComponentType<{ className?: string }>> = {
  member: Users,
  external: Globe,
  group: ShieldCheck,
  profile: KeyRound,
};

/** Iniciais para o avatar redondo (ex.: "Amanda Braga" → "AB"). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  if (parts.length === 0) return "?";
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

/** Filtro da sidebar — tipo de sujeito exibido. */
type SubjectFilter = "all" | "member" | "profile" | "external";

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------
export function KbAclManager({
  scope,
  scopeId,
  canManage = true,
  embedded = false,
  scopeLabel,
  scopeTypeLabel,
}: Props) {
  
  const { data: rows = [], isLoading } = useKbAclList(scope, scopeId);
  const grant = useKbGrantAccess();
  const revoke = useKbRevokeAccess();

  // Estado do formulário de adicionar (dentro do popover)
  const [targetType, setTargetType] = useState<KbAclTargetType>("member");
  const [targetId, setTargetId] = useState<string>("");
  const [level, setLevel] = useState<KbAclLevel>("read");
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Estado da sidebar + busca
  const [subjectFilter, setSubjectFilter] = useState<SubjectFilter>("all");
  const [search, setSearch] = useState("");

  // Fontes de dados para o combobox de alvo
  const { data: members = [], isLoading: loadingMembers } = useTenantMembersLite();
  const { data: externals = [], isLoading: loadingExternals } = useExternalUsersLite();
  const { data: profiles = [], isLoading: loadingProfiles } = usePermissionProfilesLite();

  // Alvos já concedidos → evita duplicidade no combobox
  const alreadyGranted = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => s.add(`${r.target_type}:${r.target_id}`));
    return s;
  }, [rows]);

  const availableOptions = useMemo(() => {
    if (targetType === "member") return members.filter((m) => !alreadyGranted.has(`member:${m.id}`));
    if (targetType === "external") return externals.filter((m) => !alreadyGranted.has(`external:${m.id}`));
    if (targetType === "profile") return profiles.filter((m) => !alreadyGranted.has(`profile:${m.id}`));
    return [];
  }, [targetType, members, externals, profiles, alreadyGranted]);

  // Contadores para a sidebar
  const counts = useMemo(() => {
    const c = { all: rows.length, member: 0, profile: 0, external: 0 };
    rows.forEach((r) => {
      if (r.target_type === "member") c.member += 1;
      else if (r.target_type === "profile") c.profile += 1;
      else if (r.target_type === "external") c.external += 1;
    });
    return c;
  }, [rows]);

  // Aplica filtros da sidebar + busca à lista
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (subjectFilter !== "all" && r.target_type !== subjectFilter) return false;
      if (q) {
        const name = (r.target_display_name ?? "").toLowerCase();
        const id = r.target_id.toLowerCase();
        if (!name.includes(q) && !id.includes(q)) return false;
      }
      return true;
    });
  }, [rows, subjectFilter, search]);

  const submit = () => {
    if (!targetId) return;
    grant.mutate(
      { scope, scopeId, targetType, targetId, accessLevel: level },
      {
        onSuccess: () => {
          setTargetId("");
          setPopoverOpen(false);
        },
      },
    );
  };

  // -------------------------------------------------------------------------
  // Modo `embedded` (dentro do KbGovernanceSidebar) — layout compacto vertical
  // sem sidebar de filtros nem header próprio.
  // -------------------------------------------------------------------------
  if (embedded) {
    return (
      <TooltipProvider delayDuration={200}>
        <div className="space-y-4 font-body">
          {canManage && (
            <GrantForm
              targetType={targetType}
              setTargetType={(t) => { setTargetType(t); setTargetId(""); }}
              targetId={targetId}
              setTargetId={setTargetId}
              level={level}
              setLevel={setLevel}
              availableOptions={availableOptions}
              loading={
                (targetType === "member" && loadingMembers) ||
                (targetType === "external" && loadingExternals) ||
                (targetType === "profile" && loadingProfiles)
              }
              onSubmit={submit}
              submitting={grant.isPending}
              compact
            />
          )}
          <div className="space-y-1.5">
            {isLoading && (
              <>
                <Skeleton className="h-14 w-full rounded-md" />
                <Skeleton className="h-14 w-full rounded-md" />
              </>
            )}
            {!isLoading && rows.length === 0 && <EmptyRow />}
            {rows.map((r) => {
              const Icon = TARGET_ICON[r.target_type] ?? Users;
              const display = r.target_display_name ?? r.target_id.slice(0, 8);
              return (
                <div
                  key={r.acl_id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate font-display">
                      {display}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {TARGET_LABEL[r.target_type]}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">
                    {LEVEL_LABEL[r.access_level]}
                  </Badge>
                  {r.inherited_from_space && (
                    <Badge variant="outline" className="text-[10px] gap-1">
                      <Lock className="h-3 w-3" /> Herdado
                    </Badge>
                  )}
                  {canManage && !r.inherited_from_space && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0"
                      onClick={() => revoke.mutate({ aclId: r.acl_id, scope, scopeId })}
                      disabled={revoke.isPending}
                      aria-label="Revogar"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // -------------------------------------------------------------------------
  // Modo dialog (padrão) — Steel Split-pane fiel ao protótipo v3
  // -------------------------------------------------------------------------

  /** Configuração das abas da sidebar. */
  const SUBJECT_TABS: Array<{
    key: SubjectFilter;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    count: number;
  }> = [
    { key: "all",      label: "Todos os acessos",   icon: ListFilter, count: counts.all },
    { key: "member",   label: "Membros da empresa", icon: Users,      count: counts.member },
    { key: "profile",  label: "Perfis de permissão", icon: KeyRound,   count: counts.profile },
    { key: "external", label: "Contatos externos",  icon: Globe,      count: counts.external },
  ];

  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-col h-full w-full bg-background font-body">
        {/* HEADER — ícone em caixa + título "Controle de acesso" */}
        <header className="px-6 md:px-8 py-5 border-b border-border shrink-0 flex items-center gap-3">
          <div className="p-2 bg-muted rounded-lg shrink-0">
            <ShieldCheck className="w-5 h-5 text-foreground/80" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground tracking-tight font-display">
              Controle de acesso
            </h1>
            {(scopeTypeLabel || scopeLabel) && (
              <p className="text-xs text-muted-foreground truncate">
                {scopeTypeLabel}{scopeLabel ? ` · ${scopeLabel}` : ""}
              </p>
            )}
          </div>
        </header>

        {/* CORPO — Split-pane */}
        <div className="flex flex-1 overflow-hidden">
          {/* SIDEBAR ESQUERDA */}
          <aside className="hidden md:flex w-72 shrink-0 border-r border-border bg-muted/30 p-4 flex-col gap-1 overflow-y-auto">
            <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Visualizar por sujeito
            </p>

            {SUBJECT_TABS.map((tab) => {
              const active = subjectFilter === tab.key;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setSubjectFilter(tab.key)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors font-medium",
                    active
                      ? "bg-background border border-border shadow-sm text-foreground font-semibold"
                      : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                  )}
                >
                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 text-left truncate">{tab.label}</span>
                  <span
                    className={cn(
                      "text-xs px-2 py-0.5 rounded-full",
                      active
                        ? "bg-muted text-foreground font-semibold"
                        : "text-muted-foreground",
                    )}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </aside>

          {/* MAIN — Toolbar + Tabela + Footer */}
          <main className="flex-1 flex flex-col bg-background overflow-hidden min-w-0">
            {/* Toolbar */}
            <div className="px-6 py-4 flex items-center justify-between border-b border-border shrink-0 gap-3">
              <div className="relative flex-1 max-w-md min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar membros..."
                  className="pl-9 h-9 bg-muted/40 border-transparent focus-visible:border-border"
                />
              </div>
              {canManage && (
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button size="sm" className="h-9 gap-2 shrink-0">
                      <Plus className="w-4 h-4" strokeWidth={2.5} />
                      Conceder acesso
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-96 p-4">
                    <div className="mb-3">
                      <div className="text-sm font-semibold text-foreground font-display">
                        Nova concessão
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Escolha o sujeito e o nível de acesso.
                      </div>
                    </div>
                    <GrantForm
                      targetType={targetType}
                      setTargetType={(t) => { setTargetType(t); setTargetId(""); }}
                      targetId={targetId}
                      setTargetId={setTargetId}
                      level={level}
                      setLevel={setLevel}
                      availableOptions={availableOptions}
                      loading={
                        (targetType === "member" && loadingMembers) ||
                        (targetType === "external" && loadingExternals) ||
                        (targetType === "profile" && loadingProfiles)
                      }
                      onSubmit={submit}
                      submitting={grant.isPending}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>

            {/* Tabela scrollável */}
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 bg-background/95 backdrop-blur-md z-10">
                  <tr className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest border-b border-border">
                    <th className="px-6 py-4 font-bold">Membro / Sujeito</th>
                    <th className="px-6 py-4 font-bold text-center">Nível</th>
                    <th className="px-6 py-4 font-bold">Origem</th>
                    <th className="px-6 py-4 font-bold text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {isLoading && (
                    <>
                      {[0, 1, 2].map((i) => (
                        <tr key={i}>
                          <td className="px-6 py-4"><Skeleton className="h-10 w-56" /></td>
                          <td className="px-6 py-4"><Skeleton className="h-8 w-28 mx-auto" /></td>
                          <td className="px-6 py-4"><Skeleton className="h-6 w-20" /></td>
                          <td className="px-6 py-4 text-right"><Skeleton className="h-6 w-6 ml-auto" /></td>
                        </tr>
                      ))}
                    </>
                  )}

                  {!isLoading && filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-12">
                        <EmptyRow />
                      </td>
                    </tr>
                  )}

                  {!isLoading && filteredRows.map((r) => {
                    const display = r.target_display_name ?? r.target_id.slice(0, 8);
                    const isInherited = !!r.inherited_from_space;
                    const Icon = TARGET_ICON[r.target_type] ?? Users;
                    // Subtítulo por tipo de sujeito
                    const subtitle =
                      r.target_type === "member" || r.target_type === "external"
                        ? r.target_email ?? TARGET_LABEL[r.target_type]
                        : TARGET_LABEL[r.target_type];
                    const isPerson = r.target_type === "member" || r.target_type === "external";

                    return (
                      <tr key={r.acl_id} className="group hover:bg-muted/40 transition-colors">
                        {/* Sujeito */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {isPerson ? (
                              // v3.0.4: avatar real do membro (fallback iniciais via SmartAvatar)
                              <SmartAvatar
                                size="sm"
                                src={r.target_avatar_url ?? undefined}
                                name={display}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-foreground/70 text-xs font-bold shrink-0 overflow-hidden">
                                <Icon className="w-4 h-4" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-foreground truncate font-display">
                                {display}
                              </div>
                              <div className="text-[11px] text-muted-foreground truncate">
                                {subtitle}
                              </div>
                            </div>
                          </div>
                        </td>


                        {/* Nível */}
                        <td className="px-6 py-4 text-center">
                          {canManage && !isInherited ? (
                            <Select
                              value={r.access_level}
                              onValueChange={(v) => {
                                // Alterar nível = revogar + reconceder (mesma abordagem do v1/v2)
                                revoke.mutate(
                                  { aclId: r.acl_id, scope, scopeId },
                                  {
                                    onSuccess: () => {
                                      grant.mutate({
                                        scope,
                                        scopeId,
                                        targetType: r.target_type,
                                        targetId: r.target_id,
                                        accessLevel: v as KbAclLevel,
                                      });
                                    },
                                  },
                                );
                              }}
                            >
                              <SelectTrigger className="h-8 w-32 mx-auto text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {(Object.keys(LEVEL_LABEL) as KbAclLevel[]).map((l) => (
                                  <SelectItem key={l} value={l} className="text-xs">
                                    {LEVEL_LABEL[l]}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs font-medium text-muted-foreground">
                              {LEVEL_LABEL[r.access_level]}
                            </span>
                          )}
                        </td>

                        {/* Origem */}
                        <td className="px-6 py-4">
                          {isInherited ? (
                            <div className="flex flex-col">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-muted text-muted-foreground uppercase tracking-tighter w-fit border border-border">
                                Herdada
                              </span>
                              <span className="text-[10px] text-muted-foreground mt-1">
                                De: {r.inherited_from_space}
                              </span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary uppercase tracking-tighter">
                              Direta
                            </span>
                          )}
                        </td>

                        {/* Ação */}
                        <td className="px-6 py-4 text-right">
                          {canManage && !isInherited ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-all"
                              onClick={() => revoke.mutate({ aclId: r.acl_id, scope, scopeId })}
                              disabled={revoke.isPending}
                              aria-label="Revogar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex p-2 text-muted-foreground/30 cursor-help">
                                  <Info className="w-4 h-4" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="text-xs max-w-xs">
                                Permissões herdadas devem ser gerenciadas no objeto pai
                                (space ou coleção).
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer de status */}
            <footer className="px-6 py-3 border-t border-border bg-muted/20 flex justify-between items-center text-[11px] text-muted-foreground font-medium shrink-0">
              <div className="flex items-center gap-4">
                <span>
                  <span className="text-foreground font-semibold">{filteredRows.length}</span>
                  {" "}
                  {filteredRows.length === 1 ? "concessão exibida" : "concessões exibidas"}
                </span>
                <span className="text-muted-foreground/50">•</span>
                <span className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                  Alterações salvas automaticamente
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground/70 uppercase tracking-wider">
                  {scopeTypeLabel ?? "KB"}:{" "}
                  <span className="text-foreground/70">
                    {scopeId.slice(0, 8).toUpperCase()}
                  </span>
                </span>
              </div>
            </footer>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

/** Estado vazio (compartilhado por embedded + tabela). */
function EmptyRow() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 text-center text-muted-foreground py-4">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted border border-border">
        <ShieldCheck className="w-5 h-5 opacity-60" />
      </div>
      <div className="text-sm font-medium text-foreground font-display">
        Nenhuma concessão específica neste nível
      </div>
      <div className="text-xs max-w-sm">
        Adicione uma concessão ou utilize as herdadas do escopo pai (space/coleção).
      </div>
    </div>
  );
}

/** Formulário reaproveitado no Popover (dialog) e no modo compact (embedded). */
function GrantForm(props: {
  targetType: KbAclTargetType;
  setTargetType: (t: KbAclTargetType) => void;
  targetId: string;
  setTargetId: (v: string) => void;
  level: KbAclLevel;
  setLevel: (v: KbAclLevel) => void;
  availableOptions: Array<{ id: string; label: string; sub?: string; avatar_url?: string | null }>;
  loading: boolean;
  onSubmit: () => void;
  submitting: boolean;
  compact?: boolean;
}) {
  const {
    targetType, setTargetType, targetId, setTargetId, level, setLevel,
    availableOptions, loading, onSubmit, submitting, compact,
  } = props;

  return (
    <div className="space-y-3">
      <div className={cn("grid gap-3", compact ? "grid-cols-1 sm:grid-cols-[160px_1fr]" : "grid-cols-1")}>
        <div>
          <Label className="mb-1.5 block text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
            Tipo
          </Label>
          <Select value={targetType} onValueChange={(v) => setTargetType(v as KbAclTargetType)}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="member">
                <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5" /> {TARGET_LABEL.member}</div>
              </SelectItem>
              <SelectItem value="external">
                <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5" /> {TARGET_LABEL.external}</div>
              </SelectItem>
              <SelectItem value="profile">
                <div className="flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" /> {TARGET_LABEL.profile}</div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-0">
          <Label className="mb-1.5 block text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
            Alvo
          </Label>
          <TargetCombobox
            targetType={targetType}
            targetId={targetId}
            setTargetId={setTargetId}
            options={availableOptions}
            loading={loading}
            disabled={targetType === "group"}
          />
        </div>
      </div>

      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <Label className="mb-1.5 block text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
            Nível
          </Label>
          <Select value={level} onValueChange={(v) => setLevel(v as KbAclLevel)}>
            <SelectTrigger className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(LEVEL_LABEL) as KbAclLevel[]).map((l) => (
                <SelectItem key={l} value={l}>{LEVEL_LABEL[l]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={onSubmit} disabled={!targetId || submitting}>
          {submitting ? "Concedendo…" : "Conceder"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TargetCombobox — combobox com busca (avatar + nome + email) para o campo
// "Alvo", padrão do app (SystemUserSelector / UserSelector).
// ---------------------------------------------------------------------------
interface TargetOption {
  id: string;
  label: string;
  sub?: string;
  avatar_url?: string | null;
}

function TargetCombobox(props: {
  targetType: KbAclTargetType;
  targetId: string;
  setTargetId: (v: string) => void;
  options: TargetOption[];
  loading: boolean;
  disabled?: boolean;
}) {
  const { targetType, targetId, setTargetId, options, loading, disabled } = props;
  const [open, setOpen] = useState(false);

  const isProfile = targetType === "profile";
  const selected = options.find((o) => o.id === targetId);

  // Placeholder e texto de busca dependem do tipo escolhido
  const placeholder =
    targetType === "member"
      ? "Selecione um membro…"
      : targetType === "external"
      ? "Selecione um contato externo…"
      : targetType === "profile"
      ? "Selecione um perfil…"
      : "Selecione…";
  const searchPlaceholder =
    targetType === "member"
      ? "Buscar membro por nome ou email…"
      : targetType === "external"
      ? "Buscar externo por nome ou email…"
      : "Buscar perfil…";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          className={cn(
            "w-full h-9 justify-between font-normal",
            !selected && "text-muted-foreground",
          )}
        >
          <div className="flex items-center gap-2 truncate min-w-0">
            {selected ? (
              isProfile ? (
                <>
                  <KeyRound className="h-4 w-4 shrink-0 opacity-70" />
                  <span className="truncate">{selected.label}</span>
                </>
              ) : (
                <>
                  <SmartAvatar
                    src={selected.avatar_url ?? undefined}
                    name={selected.label}
                    email={selected.sub}
                    size="xs"
                    use3DFallback
                  />
                  <span className="truncate">{selected.label}</span>
                </>
              )
            ) : (
              <span className="truncate">
                {loading ? "Carregando…" : placeholder}
              </span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[280px] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>
              {loading ? "Carregando…" : "Nenhum resultado encontrado."}
            </CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={`${o.label} ${o.sub ?? ""} ${o.id}`}
                  onSelect={() => {
                    setTargetId(o.id);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  {isProfile ? (
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <KeyRound className="h-3.5 w-3.5" />
                    </div>
                  ) : (
                    <SmartAvatar
                      src={o.avatar_url ?? undefined}
                      name={o.label}
                      email={o.sub}
                      size="sm"
                      use3DFallback
                    />
                  )}
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-sm font-medium truncate">{o.label}</span>
                    {o.sub && (
                      <span className="text-xs text-muted-foreground truncate">
                        {o.sub}
                      </span>
                    )}
                  </div>
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      targetId === o.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
